const luminance = (rgb) => rgb.reduce((sum, value, index) => {
  const channel = value / 255;
  return sum + (channel <= 0.04045 ? channel / 12.92 : ((channel + 0.055) / 1.055) ** 2.4)
    * [0.2126, 0.7152, 0.0722][index];
}, 0);

// Matches the dark header's --background token. Alpha is composited before
// measuring contrast, so antialiased edges do not count as solid black ink.
const header = [33, 33, 34];
const headerLuminance = luminance(header);

export function needsLogoSurface({ data, width, height }) {
  let transparent = 0;
  let visible = 0;
  let lowContrast = 0;
  let whiteContrast = 0;
  let opaqueBorder = 0;
  let border = 0;

  for (let y = 0; y < height; y += 1) {
    for (let x = 0; x < width; x += 1) {
      const offset = (y * width + x) * 4;
      const alpha = data[offset + 3] / 255;
      if (alpha < 0.95) transparent += 1;
      if (x === 0 || y === 0 || x === width - 1 || y === height - 1) {
        border += 1;
        if (alpha >= 0.95) opaqueBorder += 1;
      }
      if (alpha < 0.1) continue;
      const rgb = Array.from(data.slice(offset, offset + 3));
      const onDark = luminance(rgb.map((c, i) => c * alpha + header[i] * (1 - alpha)));
      const onWhite = luminance(rgb.map(c => c * alpha + 255 * (1 - alpha)));
      visible += alpha;
      if ((Math.max(onDark, headerLuminance) + 0.05)
        / (Math.min(onDark, headerLuminance) + 0.05) < 3) lowContrast += alpha;
      if (1.05 / (onWhite + 0.05) >= 3) whiteContrast += alpha;
    }
  }

  // Opaque images and opaque perimeter usually indicate an existing backdrop.
  if (!visible || transparent / (width * height) < 0.02
    || opaqueBorder / border > 0.9) return false;
  return lowContrast / visible >= 0.6 && whiteContrast / visible >= 0.6;
}

const cache = new Map();

export function analyzeLogo(src) {
  if (cache.has(src)) return cache.get(src);
  const result = new Promise(resolve => {
    const image = new Image();
    const finish = value => {
      clearTimeout(timeout);
      image.onload = null;
      image.onerror = null;
      resolve(value);
    };
    const timeout = setTimeout(() => finish(false), 10000);
    image.crossOrigin = 'anonymous';
    image.onload = () => {
      try {
        const scale = Math.min(1, 128 / Math.max(image.naturalWidth, image.naturalHeight));
        const canvas = document.createElement('canvas');
        canvas.width = Math.max(1, Math.round(image.naturalWidth * scale));
        canvas.height = Math.max(1, Math.round(image.naturalHeight * scale));
        const context = canvas.getContext('2d', { willReadFrequently: true });
        context.drawImage(image, 0, 0, canvas.width, canvas.height);
        finish(needsLogoSurface(context.getImageData(0, 0, canvas.width, canvas.height)));
      } catch {
        // Canvas/CORS restrictions must never prevent the original logo loading.
        finish(false);
      }
    };
    image.onerror = () => finish(false);
    image.src = src;
  });
  if (cache.size >= 100) cache.delete(cache.keys().next().value);
  cache.set(src, result);
  return result;
}
